import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { IInsightMessage, IInsightMeta } from '@skystone/shared';
import { Button, ErrorBanner, Spinner } from '@/components/ui';
import { cn } from '@/lib/utils';
import { formatCostUsd } from '@/lib/format-cost';
import { InsightMarkdown } from './InsightMarkdown';

interface ErrorState {
  message: string;
  retryable: boolean;
}

export interface InsightChatHandle {
  clear: () => void;
  hasHistory: () => boolean;
}

export interface InsightChatSendResult {
  message: IInsightMessage;
  meta?: IInsightMeta;
}

export interface InsightChatProps {
  onSend: (messages: IInsightMessage[]) => Promise<InsightChatSendResult>;
  emptyStatePrompts: readonly string[];
  emptyStateCaption?: string;
  placeholder?: string;
  inputAriaLabel?: string;
  messageListClassName?: string;
  onHistoryChange?: (hasHistory: boolean) => void;
  /**
   * Fires when the session-running cost changes (after each successful
   * reply, and back to null on clear). Lets the host render its own
   * "Session: $X" indicator outside the chat body.
   */
  onSessionCostChange?: (costUsd: number | null) => void;
}

const INPUT_MAX_LENGTH = 1000;

const API_ERROR_BY_STATUS: Readonly<Record<number, ErrorState>> = {
  503: { message: 'AI insight is not configured in this environment.', retryable: false },
  400: { message: 'AI insight is not available on this tab yet.', retryable: false },
  429: { message: 'Too many requests. Please try again in a moment.', retryable: true },
  504: { message: 'The analysis took too long. Please try again.', retryable: true },
};

const GENERIC_API_ERROR: ErrorState = {
  message: 'Something went wrong while generating the analysis. Please try again.',
  retryable: true,
};

const NETWORK_ERROR: ErrorState = {
  message: 'Could not reach the server. Please try again.',
  retryable: true,
};

function isApiError(err: unknown): err is { status: number; name: string } {
  return (
    typeof err === 'object' &&
    err !== null &&
    'status' in err &&
    'name' in err &&
    (err as { name: unknown }).name === 'ApiError'
  );
}

function mapError(err: unknown): ErrorState {
  // Spread into a fresh object on every call (as the original literal-returning
  // version did) so callers storing this in state always get a new reference
  // and never share/mutate the lookup-table constants.
  if (isApiError(err)) {
    return { ...(API_ERROR_BY_STATUS[err.status] ?? GENERIC_API_ERROR) };
  }
  return { ...NETWORK_ERROR };
}

export const InsightChat = forwardRef<InsightChatHandle, InsightChatProps>(function InsightChat(
  {
    onSend,
    emptyStatePrompts,
    emptyStateCaption = 'Try one of these to get started:',
    placeholder = 'Ask a question…',
    inputAriaLabel = 'Ask a question',
    messageListClassName,
    onHistoryChange,
    onSessionCostChange,
  },
  ref,
) {
  const [history, setHistory] = useState<IInsightMessage[]>([]);
  // Parallel array indexed alongside `history`. null on user messages and on
  // assistant messages whose meta wasn't returned (older callers); IInsightMeta
  // on assistant messages from the current backend. Drives both the per-turn
  // caption and the session running total.
  const [historyMeta, setHistoryMeta] = useState<(IInsightMeta | null)[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorState | null>(null);
  const requestIdRef = useRef(0);
  const messageListRef = useRef<HTMLDivElement | null>(null);

  // Notify parent on history transitions (drives optional external "New chat" buttons).
  useEffect(() => {
    onHistoryChange?.(history.length > 0);
  }, [history.length, onHistoryChange]);

  // Session running total: sum of every assistant reply's costUsd. Null when
  // no assistant reply has provided a cost (empty state, all-null metas).
  const sessionCostUsd = useMemo<number | null>(() => {
    let total = 0;
    let sawAny = false;
    for (const m of historyMeta) {
      if (m && typeof m.costUsd === 'number') {
        total += m.costUsd;
        sawAny = true;
      }
    }
    return sawAny ? total : null;
  }, [historyMeta]);

  useEffect(() => {
    onSessionCostChange?.(sessionCostUsd);
  }, [sessionCostUsd, onSessionCostChange]);

  // Auto-scroll on new content.
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [history.length, loading]);

  const clear = useCallback(() => {
    requestIdRef.current += 1;
    setHistory([]);
    setHistoryMeta([]);
    setDraft('');
    setError(null);
    setLoading(false);
  }, []);

  useImperativeHandle(ref, () => ({
    clear,
    hasHistory: () => history.length > 0,
  }), [clear, history.length]);

  const send = useCallback(async (userText: string, replacementHistory?: IInsightMessage[]) => {
    if (loading) return;
    const trimmed = userText.trim();
    if (!trimmed) return;

    const baseHistory = replacementHistory ?? history;
    const nextHistory: IInsightMessage[] = [
      ...baseHistory,
      { role: 'user', content: trimmed },
    ];
    setHistory(nextHistory);
    // Keep historyMeta aligned: trim to baseHistory length (drops anything
    // beyond the cut on retry), then push null for the new user message.
    setHistoryMeta((prev) => [...prev.slice(0, baseHistory.length), null]);
    setDraft('');
    setError(null);
    setLoading(true);

    const myId = requestIdRef.current + 1;
    requestIdRef.current = myId;

    try {
      const response = await onSend(nextHistory);
      if (myId !== requestIdRef.current) return;
      setHistory((prev) => [...prev, response.message]);
      setHistoryMeta((prev) => [...prev, response.meta ?? null]);
    } catch (err: unknown) {
      if (myId !== requestIdRef.current) return;
      setError(mapError(err));
    } finally {
      if (myId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [loading, history, onSend]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    send(draft);
  }, [send, draft]);

  const handleExampleClick = useCallback((prompt: string) => {
    send(prompt);
  }, [send]);

  const handleRetry = useCallback(() => {
    if (loading) return;
    const lastUserIdx = [...history].reverse().findIndex((m) => m.role === 'user');
    if (lastUserIdx === -1) return;
    const cutFromEnd = lastUserIdx + 1;
    const trimmed = history.slice(0, history.length - cutFromEnd);
    const lastUserMsg = history[history.length - cutFromEnd];
    send(lastUserMsg.content, trimmed);
  }, [loading, history, send]);

  const isEmpty = history.length === 0;

  return (
    <div className="space-y-3">
      {isEmpty && !loading && !error && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">{emptyStateCaption}</p>
          <div className="flex flex-wrap gap-2">
            {emptyStatePrompts.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => handleExampleClick(p)}
                className="rounded-full border border-border bg-background px-3 py-1 text-xs hover:bg-accent hover:text-accent-foreground transition-colors sm:text-sm"
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div
          ref={messageListRef}
          aria-live="polite"
          className={cn(
            'space-y-3 overflow-y-auto pr-1 [scrollbar-gutter:stable]',
            messageListClassName ?? 'max-h-[480px]',
          )}
        >
          {history.map((msg, idx) => {
            const meta = historyMeta[idx] ?? null;
            return (
              <div
                key={`${msg.role}-${idx}`}
                className={cn('flex flex-col', msg.role === 'user' ? 'items-end' : 'items-start')}
              >
                <div
                  className={cn(
                    'rounded-lg px-3 py-2 text-sm',
                    msg.role === 'user'
                      // Outgoing user messages stay narrow so they read like
                      // questions, not paragraphs.
                      ? 'max-w-[90%] bg-primary text-primary-foreground sm:max-w-[80%] md:max-w-[75%] lg:max-w-[70%] xl:max-w-[60%]'
                      // Assistant replies (which often contain tables) get more
                      // room — tables wrap badly otherwise.
                      : 'max-w-[95%] bg-muted text-foreground md:max-w-[92%] lg:max-w-[90%]',
                  )}
                >
                  {msg.role === 'assistant' ? (
                    <InsightMarkdown content={msg.content} />
                  ) : (
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  )}
                </div>
                {msg.role === 'assistant' && meta && (
                  <ReplyMetaCaption meta={meta} />
                )}
              </div>
            );
          })}

          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner size="sm" />
              <span>Analyzing…</span>
            </div>
          )}
        </div>
      )}

      {loading && isEmpty && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner size="sm" />
          <span>Analyzing…</span>
        </div>
      )}

      {error && (
        <div className="space-y-2">
          <ErrorBanner message={error.message} />
          {error.retryable && history.length > 0 && (
            <Button onClick={handleRetry} variant="outline" size="sm" disabled={loading}>
              Try again
            </Button>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-2 sm:flex-row">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={INPUT_MAX_LENGTH}
          disabled={loading}
          placeholder={placeholder}
          aria-label={inputAriaLabel}
          className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
        />
        <Button
          type="submit"
          disabled={loading || draft.trim().length === 0}
          isLoading={loading}
          className="sm:w-auto"
        >
          Send
        </Button>
      </form>
    </div>
  );
});

function ReplyMetaCaption({ meta }: Readonly<{ meta: IInsightMeta }>) {
  // Latency: render to one decimal place when sub-10s ("1.4s"), otherwise
  // an integer ("12s"). Below 100ms still shows "0.1s" rather than "0s".
  const latencyText = meta.latencyMs >= 10_000
    ? `${Math.round(meta.latencyMs / 1000)}s`
    : `${(meta.latencyMs / 1000).toFixed(1)}s`;
  const cost = formatCostUsd(meta.costUsd);
  const parts = [cost, latencyText, meta.model].filter((p): p is string => Boolean(p));
  return (
    <p
      className="mt-1 truncate text-xs text-muted-foreground"
      aria-label="Reply cost, latency, and model"
    >
      {parts.join(' • ')}
    </p>
  );
}
